import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { requireAdmin } from "@/auth/requireAdmin";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Database as DatabaseIcon } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  PageContent,
  Tabs,
  TabsList,
  TabsTrigger,
} from "beproduct-ui";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/navigation/page-header";
import { JobsTable, type JobWithLastRun } from "@/components/jobs-table";
import { StatusBadge, EnabledBadge } from "@/components/status-badge";
import { WorkersCard } from "@/components/workers-card";
import { ConcurrencyCard } from "@/components/concurrency-card";
import { ImageBlobStatsCard } from "@/components/image-blob-stats-card";
import { LicenseCard } from "@/components/license-card";
import { companiesQueryOptions } from "@/auth/queryOptions";
import { isoSeconds, durationLabel } from "@/lib/format";

interface TenantDetailResponse {
  tenant: { id: string; name: string; slug: string; enabled: boolean };
  jobs: JobWithLastRun[];
  recent_runs: Array<{
    id: string;
    job_name: string | null;
    trigger: string;
    status: string;
    started_at: string | null;
    finished_at: string | null;
  }>;
}

export const Route = createFileRoute("/_authed/$companySlug/")({
  beforeLoad: ({ context, params }) => {
    requireAdmin(context, params.companySlug);
  },
  component: CompanyDashboard,
});

/**
 * Per-company landing — the dashboard equivalent for one tenant. Shows
 * jobs (with run/edit/toggle actions) + recent runs + the workers
 * panel (workers are system-wide; the badge inside makes that clear).
 */
// Tabs above the "Recent runs" table. Mirrors the Hangfire dashboard
// idea — `all` is the unfiltered last-N view; the others narrow by
// status. `queued + running` collapses into a single "active" tab
// because operators usually want one button for "is anything alive".
const RUN_TABS = [
  { value: "all",       label: "All" },
  { value: "active",    label: "Running + queued" },
  { value: "succeeded", label: "Succeeded" },
  { value: "failed",    label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
] as const;
type RunTab = (typeof RUN_TABS)[number]["value"];

// Hard cap on the dashboard's Recent runs panel — keeps the page
// fast and signals "this is a snapshot, not the full log". Use the
// dedicated /runs page for the full history with pagination.
const RECENT_RUNS_LIMIT = 20;

interface RunListRow {
  id: string;
  job_id: string | null;
  job_name: string | null;
  trigger: string;
  status: string;
  started_at: string | null;
  finished_at: string | null;
}

function CompanyDashboard() {
  const { company } = Route.useRouteContext();
  const { companySlug } = Route.useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [runTab, setRunTab] = useState<RunTab>("all");

  // Global licensing toggle from the same cached query that resolves
  // companies. Hides the LicenseCard on SaaS installs where licensing
  // is off — same query, no extra round-trip.
  const companiesQ = useQuery(companiesQueryOptions);
  const licensingEnabled = companiesQ.data?.licensingEnabled ?? false;

  const { data, isLoading, error } = useQuery({
    queryKey: ["tenant", company.id],
    queryFn: () => api<TenantDetailResponse>(`/api/tenants/${company.id}`),
    refetchInterval: 5_000,
  });

  // Server-driven filter via /api/runs?tenant=&status=. `active`
  // expands to two server calls; everything else maps 1:1 to a
  // single status. Refetches at the dashboard cadence so a job that
  // transitions out of `running` falls off the Active tab quickly.
  const runsQ = useQuery({
    queryKey: ["runs", company.id, runTab],
    queryFn: async () => {
      if (runTab === "active") {
        const [q, r] = await Promise.all([
          api<{ runs: RunListRow[] }>(
            `/api/runs?tenant=${encodeURIComponent(company.id)}&status=queued&limit=${RECENT_RUNS_LIMIT}`,
          ),
          api<{ runs: RunListRow[] }>(
            `/api/runs?tenant=${encodeURIComponent(company.id)}&status=running&limit=${RECENT_RUNS_LIMIT}`,
          ),
        ]);
        // Re-merge, sorted by started_at desc (nulls first — queued).
        const merged = [...r.runs, ...q.runs];
        merged.sort((a, b) => {
          const aT = a.started_at ?? "";
          const bT = b.started_at ?? "";
          return bT.localeCompare(aT);
        });
        return { runs: merged.slice(0, RECENT_RUNS_LIMIT) };
      }
      const status = runTab === "all" ? "" : `&status=${runTab}`;
      return api<{ runs: RunListRow[] }>(
        `/api/runs?tenant=${encodeURIComponent(company.id)}${status}&limit=${RECENT_RUNS_LIMIT}`,
      );
    },
    refetchInterval: 5_000,
  });

  const toggleTenant = useMutation({
    mutationFn: () =>
      api(`/api/tenants/${company.id}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled: !(data?.tenant.enabled ?? true) }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", company.id] });
      qc.invalidateQueries({ queryKey: ["auth", "companies"] });
    },
  });

  if (error) {
    return (
      <PageContent>
        <div className="p-8 text-destructive">Failed to load company.</div>
      </PageContent>
    );
  }
  if (isLoading || !data) {
    return (
      <PageContent>
        <div className="p-8 text-muted-foreground">Loading…</div>
      </PageContent>
    );
  }
  const { tenant, jobs } = data;

  return (
    <PageContent>
      <div className="pt-4 pb-8 md:pt-6 space-y-6">
        <PageHeader title={tenant.name} status={<EnabledBadge enabled={tenant.enabled} />} actions={
          <div className="flex items-center gap-2">
            <Button asChild>
              <Link to="/$companySlug/jobs/new" params={{ companySlug }}>
                <Plus className="size-4" /> Add workflow
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/$companySlug/schema" params={{ companySlug }} search={{ table: undefined }}>
                <DatabaseIcon className="size-4" /> Schema
              </Link>
            </Button>
            <Button variant="outline" onClick={() => toggleTenant.mutate()}>
              {tenant.enabled ? "Disable sync" : "Enable sync"}
            </Button>
            {/* Delete moved to Settings → Companies — destructive ops
                live in one place so the dashboard stays operational. */}
          </div>
        } />

        <WorkersCard />
        <ConcurrencyCard tenantId={company.id} />
        <ImageBlobStatsCard tenantId={company.id} companySlug={companySlug} />
        {licensingEnabled && (
          <LicenseCard tenantId={company.id} companySlug={companySlug} />
        )}

        <div>
          <h2 className="text-lg font-semibold mb-3">Workflows ({jobs.length})</h2>
          <JobsTable
            companySlug={companySlug}
            jobs={jobs}
            invalidateKey={["tenant", company.id] as const}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="flex items-baseline gap-2">
              <h2 className="text-lg font-semibold">Recent runs</h2>
              <span className="text-xs text-muted-foreground">
                latest {RECENT_RUNS_LIMIT} · full log on the{" "}
                <Link
                  to="/$companySlug/runs"
                  params={{ companySlug }}
                  className="underline hover:text-foreground"
                >
                  Runs page
                </Link>
              </span>
            </div>
            <Tabs value={runTab} onValueChange={(v) => setRunTab(v as RunTab)}>
              <TabsList>
                {RUN_TABS.map((t) => (
                  <TabsTrigger key={t.value} value={t.value}>
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                  <tr className="border-b">
                    <th className="text-left p-3">Workflow</th>
                    <th className="text-left p-3">Trigger</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Started</th>
                    <th className="text-left p-3">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {!runsQ.data ? (
                    <tr>
                      <td colSpan={5} className="text-muted-foreground text-center py-6">
                        {runsQ.isLoading ? "Loading…" : "—"}
                      </td>
                    </tr>
                  ) : runsQ.data.runs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-muted-foreground text-center py-6">
                        {runTab === "all"
                          ? "No runs yet."
                          : runTab === "active"
                            ? "Nothing running or queued."
                            : `No ${runTab} runs.`}
                      </td>
                    </tr>
                  ) : (
                    runsQ.data.runs.map((r) => (
                      <tr
                        key={r.id}
                        className="border-b last:border-0 hover:bg-muted/50 cursor-pointer"
                        onClick={() =>
                          nav({
                            to: "/$companySlug/runs/$runId",
                            params: { companySlug, runId: r.id },
                          })
                        }
                      >
                        <td className="p-3">
                          {r.job_name ?? <span className="text-muted-foreground">ad-hoc</span>}
                        </td>
                        <td className="p-3 text-muted-foreground">{r.trigger}</td>
                        <td className="p-3"><StatusBadge status={r.status} /></td>
                        <td className="p-3 text-muted-foreground">{isoSeconds(r.started_at)}</td>
                        <td className="p-3 text-muted-foreground">
                          {durationLabel(r.started_at, r.finished_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageContent>
  );
}

